import { useCallback, useEffect, useRef, useState } from "react";
import { GptLiveInputContext, GptLiveInputRouter } from "../utils/gptLiveInputRouter";

const ENGLISH_LANGUAGE_INSTRUCTIONS =
  "This application currently supports English only. Speak and respond in English " +
  "throughout this session. Interpret user speech as English, including short " +
  "homework numbers. Do not switch languages because of an accent, an ambiguous " +
  "word, or background audio. If speech is unclear, ask the user to repeat it in " +
  "English rather than guessing. If another language is requested, briefly explain " +
  "in English that only English is currently supported. Preserve names and the " +
  "existing homework workflow and delegation rules. Apply this preference silently; " +
  "do not announce it or start a new conversation.";

const GREETING_INSTRUCTIONS =
  "The application handles the initial voice introduction once per chat conversation. " +
  "Do not introduce yourself again when the user says hi or reconnects voice. " +
  "Remain silent after reconnecting until the user speaks. " +
  "Do not acknowledge the connection or restored context with speech, including " +
  "Mm-hmm, okay, or I'm listening. Connection sounds and background noise are not user speech. " +
  "Continue to deliver application-requested spoken results normally. " +
  "For a standalone hello in an ongoing conversation, respond briefly: " +
  "Hi again! What would you like help with? " +
  "If the hello includes a request, respond directly to that request. " +
  "If the user starts speaking before the initial introduction, prioritize their " +
  "utterance and skip the scripted introduction. Preserve all workflow and delegation rules.";

const INITIAL_GREETING =
  "Hi, I'm your OCD homework assistant. What would you like help with today?";

export interface GptLiveClientDelegation {
  delegationId: string;
  transcript: string;
  offsetMs: number | null;
}

export interface GptLiveVoiceOptions {
  enabled?: boolean;
  getConversationId?: () => string;

  onClientDelegation?: (
    delegation: GptLiveClientDelegation
  ) => void | Promise<void>;

  // Fallback for spoken ABC Game turns that GPT-Live
  // transcribes but does not delegate.
  onUndelegatedGameTurn?: (
    transcript: string
  ) => boolean;

  getInputContext?: () => GptLiveInputContext;
  onUndelegatedHomeworkChoice?: (selection: string) => boolean;

  // Returns the latest authoritative React/OCD state
  // whenever a new GPT-Live session starts.
  getRehydrationContext?: () => string | null;
}

export interface GptLiveVoiceHandle {
  supported: boolean;
  isConnecting: boolean;
  isConnected: boolean;

  start: () => Promise<void>;
  stop: () => Promise<void>;

  sendCommentary: (
    delegationId: string | null,
    content: string
  ) => boolean;

  sendSilentUpdate: (
    delegationId: string,
    content: string
  ) => boolean;

  sendExactSpeech: (
    delegationId: string,
    content: string
  ) => boolean;
}

export function useGptLiveVoice(
  options: GptLiveVoiceOptions = {}
): GptLiveVoiceHandle {
  const {
    enabled = false,
    onClientDelegation,
    onUndelegatedGameTurn,
    getRehydrationContext,
  } = options;

  // Keep the latest delegation handler available to the WebRTC
  // event listener without rebuilding the connection.
  const onClientDelegationRef =
    useRef<GptLiveVoiceOptions["onClientDelegation"]>(
      onClientDelegation
    );

  useEffect(() => {
    onClientDelegationRef.current = onClientDelegation;
  }, [onClientDelegation]);

  const onUndelegatedGameTurnRef =
    useRef<GptLiveVoiceOptions["onUndelegatedGameTurn"]>(
      onUndelegatedGameTurn
    );

  useEffect(() => {
    onUndelegatedGameTurnRef.current =
      onUndelegatedGameTurn;
  }, [onUndelegatedGameTurn]);

  const getRehydrationContextRef =
    useRef<GptLiveVoiceOptions["getRehydrationContext"]>(
      getRehydrationContext
    );

  useEffect(() => {
    getRehydrationContextRef.current =
      getRehydrationContext;
  }, [getRehydrationContext]);

  const peerRef = useRef<RTCPeerConnection | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const dataChannelRef = useRef<RTCDataChannel | null>(null);
  const remoteAudioRef = useRef<HTMLAudioElement | null>(null);
  // Voice connections are disposable; greeting history belongs to the chat.
  const greetedConversationsRef = useRef(new Set<string>());
  const greetingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const inputOptionsRef = useRef(options);
  inputOptionsRef.current = options;
  const inputRouterRef = useRef<GptLiveInputRouter | null>(null);
  if (!inputRouterRef.current) {
    inputRouterRef.current = new GptLiveInputRouter({
      getContext: () => inputOptionsRef.current.getInputContext?.() ?? {
        scopeKey: "gpt-live-session",
      },
      onDelegation: (event) => onClientDelegationRef.current?.(event),
      onHomeworkChoice: (selection) =>
        inputOptionsRef.current.onUndelegatedHomeworkChoice?.(selection) === true,
      onGameTurn: (transcript) =>
        onUndelegatedGameTurnRef.current?.(transcript) === true,
      onDuplicate: (delegationId, reason) => {
        const dc = dataChannelRef.current;
        if (dc?.readyState !== "open") return;
        dc.send(JSON.stringify({
          type: "session.thinking.append",
          event_id: `gpt-live-consumed-${delegationId}`,
          delegation_id: delegationId,
          content: reason ?? "The application already handled this user turn. Do not repeat the action.",
        }));
      },
      onError: (error) => console.error("[GPT-Live] input routing failed", error),
    });
  }

  const [isConnecting, setIsConnecting] = useState(false);
  const [isConnected, setIsConnected] = useState(false);

  const supported =
    typeof window !== "undefined" &&
    typeof RTCPeerConnection !== "undefined" &&
    typeof navigator !== "undefined" &&
    Boolean(navigator.mediaDevices?.getUserMedia);

  const releaseResources = useCallback(() => {

    if (greetingTimerRef.current !== null) {
      clearTimeout(greetingTimerRef.current);
      greetingTimerRef.current = null;
    }

    // Do not let transcripts or pending selections cross voice connections.
    inputRouterRef.current?.clear();
    const dataChannel = dataChannelRef.current;
    dataChannelRef.current = null;

    if (dataChannel) {
      try {
        dataChannel.close();
      } catch (error) {
        console.warn(
          "[GPT-Live] data channel cleanup failed",
          error
        );
      }
    }

    const peer = peerRef.current;
    peerRef.current = null;

    if (peer) {
      try {
        peer.close();
      } catch (error) {
        console.warn(
          "[GPT-Live] peer cleanup failed",
          error
        );
      }
    }

    const stream = streamRef.current;
    streamRef.current = null;

    if (stream) {
      for (const track of stream.getTracks()) {
        try {
          track.stop();
        } catch (error) {
          console.warn(
            "[GPT-Live] microphone cleanup failed",
            error
          );
        }
      }
    }

    const remoteAudio = remoteAudioRef.current;
    remoteAudioRef.current = null;

    if (remoteAudio) {
      try {
        remoteAudio.pause();
      } catch (error) {
        console.warn(
          "[GPT-Live] remote audio pause failed",
          error
        );
      }

      try {
        remoteAudio.srcObject = null;
      } catch (error) {
        console.warn(
          "[GPT-Live] remote audio cleanup failed",
          error
        );
      }
    }

  }, []);

  const stop = useCallback(async () => {
    releaseResources();

    setIsConnected(false);
    setIsConnecting(false);
  }, [releaseResources]);

  /**
   * Return a client-delegated backend result to GPT-Live.
   *
   * The delegationId must be the id received from
   * session.delegation.created.
   */
  const sendCommentary = useCallback(
    (
      delegationId: string | null,
      content: string
    ): boolean => {
      const dc = dataChannelRef.current;

      const safeDelegationId =
        delegationId === null
          ? null
          : delegationId.trim();

      const safeContent =
        (content || "").trim();

      // A null delegation ID is valid for UI-originated actions.
      if (delegationId !== null && !safeDelegationId) {
        console.warn(
          "[GPT-Live] sendCommentary missing delegationId"
        );
        return false;
      }

      if (!safeContent) {
        console.warn(
          "[GPT-Live] sendCommentary missing content"
        );
        return false;
      }

      if (!dc || dc.readyState !== "open") {
        console.warn(
          "[GPT-Live] sendCommentary called while data channel is not open"
        );
        return false;
      }

      const eventId =
        `gpt-live-commentary-${Date.now()}`;

      dc.send(
        JSON.stringify({
          type: "session.commentary.append",
          event_id: eventId,
          delegation_id: safeDelegationId,
          content: safeContent,
        })
      );

      console.info(
        "[GPT-Live] commentary sent",
        {
          eventId,
          delegationId: safeDelegationId,
          contentLength: safeContent.length,
        }
      );

      return true;
    },
    []
  );


  // --------------------------------------------------------
  // GPT-LIVE: SILENT LOCAL UI UPDATE
  // --------------------------------------------------------
  // Use for Start, Pause, Resume, Reset on Coach Skills.
  // The widget owns its prerecorded audio narration.

  const sendSilentUpdate = useCallback(
    (
      delegationId: string,
      content: string
    ): boolean => {
      const dc = dataChannelRef.current;
      const id = (delegationId || "").trim();
      const text = (content || "").trim();

      if (!id || !text || !dc || dc.readyState !== "open") {
        console.warn(
          "[GPT-Live] silent update unavailable",
          { delegationId }
        );
        return false;
      }

      const eventId =
        `gpt-live-silent-${Date.now()}`;

      dc.send(
        JSON.stringify({
          type: "session.thinking.append",
          event_id: eventId,
          delegation_id: id,
          content: text,
        })
      );

      console.info(
        "[GPT-Live] silent update sent",
        { delegationId: id }
      );

      return true;
    },
    []
  );


  /**
 * Ask GPT-Live to speak a delegated result exactly as written.
 *
 * Use this only for UI experiences where the spoken text must
 * stay synchronized with authoritative on-screen text, such as
 * ABC Game "Buddy says".
 *
 * Normal OCD v4 responses should continue using sendCommentary().
 */
  const sendExactSpeech = useCallback(
    (
      delegationId: string,
      content: string
    ): boolean => {
      const dc = dataChannelRef.current;

      const safeDelegationId =
        (delegationId || "").trim();

      const safeContent =
        (content || "").trim();

      if (!safeDelegationId) {
        console.warn(
          "[GPT-Live] sendExactSpeech missing delegationId"
        );
        return false;
      }

      if (!safeContent) {
        console.warn(
          "[GPT-Live] sendExactSpeech missing content"
        );
        return false;
      }

      if (!dc || dc.readyState !== "open") {
        console.warn(
          "[GPT-Live] sendExactSpeech called while data channel is not open"
        );
        return false;
      }

      const eventId =
        `gpt-live-abc-result-${Date.now()}`;

      // Return the authoritative game response as a
      // spoken result, rather than a new instruction.
      dc.send(
        JSON.stringify({
          type: "session.commentary.append",
          event_id: eventId,
          delegation_id: safeDelegationId,
          content: safeContent,
        })
      );

      console.info(
        "[GPT-Live] ABC Game commentary sent",
        {
          eventId,
          delegationId: safeDelegationId,
          contentLength: safeContent.length,
        }
      );

      return true;
    },
    []
  );

  const start = useCallback(async () => {
    if (!enabled) {
      console.warn(
        "[GPT-Live] start() called while disabled"
      );
      return;
    }

    if (!supported) {
      console.warn(
        "[GPT-Live] WebRTC audio unsupported"
      );
      return;
    }

    if (
      peerRef.current ||
      isConnecting ||
      isConnected
    ) {
      return;
    }

    setIsConnecting(true);

    try {
      const pc = new RTCPeerConnection();
      peerRef.current = pc;

      const dc =
        pc.createDataChannel("oai-events");

      dataChannelRef.current = dc;
      const conversationId =
        inputOptionsRef.current.getConversationId?.() ?? "default-conversation";
      let sessionInitialized = false;

      dc.onopen = () => {
        console.info(
          "[GPT-Live] data channel open"
        );
      };

      dc.onclose = () => {
        console.info(
          "[GPT-Live] data channel closed"
        );
      };

      dc.onerror = (event) => {
        console.error(
          "[GPT-Live] data channel error",
          event
        );
      };

      dc.onmessage = (event) => {
        if (dataChannelRef.current !== dc) return;
        try {
          const parsed =
            JSON.parse(event.data);

          console.debug(
            "[GPT-Live] event",
            parsed?.type ?? "unknown",
            parsed
          );

          // --------------------------------------------------
          // USER SPEECH TRANSCRIPT
          // --------------------------------------------------
          if (
            parsed?.type ===
            "session.input_transcript.delta" &&
            typeof parsed?.delta === "string"
          ) {
            if (parsed.delta.trim()) {
              greetedConversationsRef.current.add(conversationId);
              if (greetingTimerRef.current !== null) {
                clearTimeout(greetingTimerRef.current);
                greetingTimerRef.current = null;
              }
            }
            inputRouterRef.current?.input(
              parsed.delta,
              typeof parsed.end_ms === "number" && Number.isFinite(parsed.end_ms)
                ? parsed.end_ms : null,
            );
          }

          if (
            parsed?.type === "session.output_transcript.delta" &&
            typeof parsed?.delta === "string"
          ) {
            inputRouterRef.current?.output(
              typeof parsed.start_ms === "number" && Number.isFinite(parsed.start_ms)
                ? parsed.start_ms : null,
            );
          }

          // --------------------------------------------------
          // SESSION STARTED
          // --------------------------------------------------
          if (
            parsed?.type ===
            "session.started" && !sessionInitialized && dc.readyState === "open"
          ) {
            sessionInitialized = true;
            // Apply on fresh sessions and reconnects, independently of chat history.
            // Append instructions so the server's workflow rules remain intact.
            dc.send(JSON.stringify({
              type: "session.instructions.append",
              event_id: `gpt-live-language-en-${Date.now()}`,
              delegation_id: null,
              content: `${ENGLISH_LANGUAGE_INSTRUCTIONS}\n\n${GREETING_INSTRUCTIONS}`,
            }));

            setIsConnected(true);
            setIsConnecting(false);

            console.info(
              "[GPT-Live] session started"
            );

            // ------------------------------------------------
            // REHYDRATE CONTINUATION STATE
            //
            // A reconnect creates a fresh GPT-Live session.
            // Restore only the compact authoritative state
            // needed to continue the existing OCD session.
            //
            // session.thinking.append is intentionally silent.
            // ------------------------------------------------
            const rawContext =
              getRehydrationContextRef.current?.() ?? "";

            const rehydrationContext =
              rawContext.trim();

            if (
              rehydrationContext &&
              dc.readyState === "open"
            ) {
              // Keep comfortably below the Live append limit.
              const safeContext =
                rehydrationContext.slice(0, 1600);

              const eventId =
                `gpt-live-rehydrate-${Date.now()}`;

              dc.send(
                JSON.stringify({
                  type: "session.thinking.append",
                  event_id: eventId,
                  delegation_id: null,
                  content: safeContext,
                })
              );

              console.info(
                "[GPT-Live] continuation state restored",
                {
                  eventId,
                  contentLength: safeContext.length,
                }
              );
            }

            if (!greetedConversationsRef.current.has(conversationId)) {
              // Give immediate user speech a chance to arrive before greeting.
              greetingTimerRef.current = setTimeout(() => {
                greetingTimerRef.current = null;
                if (dataChannelRef.current !== dc || dc.readyState !== "open" ||
                    (inputOptionsRef.current.getConversationId?.() ?? "default-conversation") !== conversationId ||
                    greetedConversationsRef.current.has(conversationId)) return;
                dc.send(JSON.stringify({
                  type: "session.commentary.append",
                  event_id: `gpt-live-greeting-${Date.now()}`,
                  delegation_id: null,
                  content: INITIAL_GREETING,
                }));
                greetedConversationsRef.current.add(conversationId);
              }, 1000);
            }
          }

          // --------------------------------------------------
          // CLIENT DELEGATION
          // --------------------------------------------------
          if (
            parsed?.type ===
            "session.delegation.created" &&
            parsed?.delegation?.target ===
            "client"
          ) {

            inputRouterRef.current?.delegation(
              String(parsed?.delegation?.id ?? "").trim(),
              typeof parsed.offset_ms === "number" && Number.isFinite(parsed.offset_ms)
                ? parsed.offset_ms : null,
            );
          }
        } catch {
          console.debug(
            "[GPT-Live] raw event",
            event.data
          );
        }
      };

      pc.ontrack = (event) => {
        if (
          !remoteAudioRef.current
        ) {
          remoteAudioRef.current =
            new Audio();

          remoteAudioRef.current.autoplay =
            true;
        }

        const stream =
          event.streams?.[0];

        if (stream) {
          remoteAudioRef.current.srcObject =
            stream;
        }
      };

      pc.onconnectionstatechange =
        () => {
          console.info(
            "[GPT-Live] peer connection state:",
            pc.connectionState
          );

          if (
            pc.connectionState ===
            "failed" ||
            pc.connectionState ===
            "closed" ||
            pc.connectionState ===
            "disconnected"
          ) {
            setIsConnected(false);
          }
        };

      // ------------------------------------------------------
      // MICROPHONE
      // ------------------------------------------------------
      const stream =
        await navigator.mediaDevices.getUserMedia(
          {
            audio: true,
          }
        );

      streamRef.current = stream;

      for (
        const track of
        stream.getAudioTracks()
      ) {
        pc.addTrack(
          track,
          stream
        );
      }

      // ------------------------------------------------------
      // CREATE WEBRTC OFFER
      // ------------------------------------------------------
      const offer =
        await pc.createOffer();

      await pc.setLocalDescription(
        offer
      );

      // Wait for ICE gathering so the SDP includes candidates.
      if (
        pc.iceGatheringState !==
        "complete"
      ) {
        await new Promise<void>(
          (resolve) => {
            const handleIceGatheringStateChange =
              () => {
                if (
                  pc.iceGatheringState ===
                  "complete"
                ) {
                  pc.removeEventListener(
                    "icegatheringstatechange",
                    handleIceGatheringStateChange
                  );

                  resolve();
                }
              };

            pc.addEventListener(
              "icegatheringstatechange",
              handleIceGatheringStateChange
            );
          }
        );
      }

      const localSdp =
        pc.localDescription?.sdp;

      if (!localSdp) {
        throw new Error(
          "[GPT-Live] local SDP offer is unavailable"
        );
      }

      // ------------------------------------------------------
      // CREATE GPT-LIVE SESSION THROUGH TRUSTED SERVER
      //
      // Endpoint contract:
      // POST browser SDP as application/sdp
      //
      // Expected response:
      // {
      //   session: { id },
      //   transport: {
      //     type: "webrtc",
      //     sdp: "..."
      //   }
      // }
      // ------------------------------------------------------
      let response: Response;

      try {
        const sessionUrl =
          process.env
            .REACT_APP_GPT_LIVE_SESSION_URL
            ?.trim();

        if (!sessionUrl) {
          throw new Error(
            "[GPT-Live] REACT_APP_GPT_LIVE_SESSION_URL is not configured"
          );
        }

        response =
          await fetch(
            sessionUrl,
            {
              method: "POST",
              headers: {
                "Content-Type":
                  "application/sdp",
              },
              body: localSdp,
            }
          );
      } catch (error) {
        console.error(
          "[GPT-Live] session endpoint failed",
          error
        );

        throw error;
      }

      if (!response.ok) {
        console.error(
          "[GPT-Live] session endpoint failed",
          {
            status:
              response.status,
            statusText:
              response.statusText,
          }
        );

        throw new Error(
          `[GPT-Live] session endpoint failed (${response.status})`
        );
      }

      const data =
        await response.json();

      const remoteSdp =
        data?.transport?.sdp;

      if (
        typeof remoteSdp !==
        "string" ||
        !remoteSdp.trim()
      ) {
        throw new Error(
          "[GPT-Live] session response missing transport.sdp"
        );
      }

      // ------------------------------------------------------
      // APPLY OPENAI'S SDP ANSWER
      // ------------------------------------------------------
      await pc.setRemoteDescription(
        {
          type: "answer",
          sdp: remoteSdp,
        }
      );

      console.info(
        "[GPT-Live] SDP answer applied",
        {
          sessionId:
            data?.session?.id ??
            null,
        }
      );
    } catch (error) {
      console.error(
        "[GPT-Live] connection failed",
        error
      );

      releaseResources();

      setIsConnected(false);
      setIsConnecting(false);
    }
  }, [
    enabled,
    isConnected,
    isConnecting,
    releaseResources,
    supported,
  ]);

  // --------------------------------------------------------
  // CLEANUP ON UNMOUNT
  // --------------------------------------------------------
  useEffect(() => {
    return () => {
      releaseResources();
    };
  }, [releaseResources]);

  return {
    supported,
    isConnecting,
    isConnected,
    start,
    stop,
    sendCommentary,
    sendSilentUpdate,
    sendExactSpeech,
  };
}
